import React, { PropsWithChildren, useContext, useEffect, useLayoutEffect, useCallback, useMemo, useRef, useState } from "react";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { usePost } from "../../API";
import { useParams, useHistory } from "react-router-dom";
import { useLocalizedStrings } from "../../Localization";
import { Alert, Button, ChatMessageView, CodeDisplay, Loading, ProgressCircle, ResizablePanel, Spinner, Card, CardBody } from "@aidkitorg/component-library";
import { AIConversation, ChatMessageItemTypes, ContentPartTypes } from "@aidkitorg/types/lib/convoai";
import { DEFAULT_AGENT_NAME, DEFAULT_AVATAR_URL, MessageContent } from "../AICoachUI";
import { PublicConfigurationContext } from "../../Context";
import { InlineScoring, ScoreRow } from "./InlineScoring";
import { InlineTags } from "./InlineTags";
import { ModularQuestionPage } from "../../ModularQuestionPage";
import { Sections } from "../../Apply";
import { ApplicantProvider } from "../../Contexts/ApplicantContext";
import { InfoDict } from "../../Questions/Props";
import type { Model as ScoreModel } from "@aidkitorg/aidkit/lib/convoai/schemas/Score";

type Interaction = {
  appState?: ChatMessageItemTypes["appState"],
  userMessages: ChatMessageItemTypes['message'][],
  coachMessage: ChatMessageItemTypes['message'],
};

export type ConversationReviewContextType = {
  enabled: boolean,
  conversation?: AIConversation,
  interactions: Interaction[],
  activeInteraction?: Interaction,
  scoresMap: Record<string, ScoreModel>,
  /** Map of conversation item IDs to their tags array */
  tagsMap: Record<string, string[]>,
  setScoresMap: (v: ConversationReviewContextType["scoresMap"]) => void,
  setTagsMap: (v: ConversationReviewContextType["tagsMap"]) => void,
  updateScore: (id: string, update: Partial<ScoreModel>) => void,
  getScoresForMessageIds: (messageIds: ScoreModel["message_ids"]) => ScoreModel[],
  setConversation: (v: AIConversation) => void,
  setActiveInteraction: (v:Interaction) => void,
};

const defaultConversationReviewState : ConversationReviewContextType = {
  enabled: false,
  interactions: [] as Interaction[],
  scoresMap: {},
  tagsMap: {},
  setScoresMap: () => console.warn('no conversation review context setup, ignoring setScoresMap'),
  setTagsMap: () => console.warn('no conversation review context setup, ignoring setTagsMap'),
  updateScore: () => console.warn('no conversation review context setup, ignoring updateScore'),
  getScoresForMessageIds: () => [],
  setConversation: () => console.warn('no conversation review context setup, ignoring setConversation'),
  setActiveInteraction: () => console.warn('no conversation review context setup, ignoring setActiveInteraction')
}
export const ConversationReviewContext = React.createContext<ConversationReviewContextType>(defaultConversationReviewState);
export const ConversationReviewProvider = ({ children }: PropsWithChildren) => {
  const config = useContext(PublicConfigurationContext);
  const [scoresMap, setScoresMap] = useState<Record<string, ScoreModel>>({});
  /** Map of conversation item IDs to their tags */
  const [tagsMap, setTagsMap] = useState<Record<string, string[]>>({});
  const [conversation, setConversation] = useState<AIConversation | undefined>();
  const [activeInteraction, setActiveInteraction] = useState<Interaction | undefined>();
  const enabled = true;
  
  const interactions: Interaction[] = useMemo(() => {
    const result: Interaction[] = [];
    (conversation?.items ?? []).forEach((item, i, arr) => {
      if (item.type === "message" && item.role === "assistant") {
        const interaction: Interaction = {
          userMessages: [] as ChatMessageItemTypes['message'][],
          coachMessage: item,
        }

        // walk backwards and gather appState and user messages
        while (true) {
          i--;
          const prevItem = arr[i] as ChatMessageItemTypes['message'] | ChatMessageItemTypes["appState"];
          if (!prevItem || (prevItem as ChatMessageItemTypes["message"]).role === "assistant") {
            break;
          }
          else if ((prevItem as ChatMessageItemTypes["message"]).role === "user") {
            interaction.userMessages.unshift(prevItem as ChatMessageItemTypes["message"]);
          }
          else if ((prevItem as ChatMessageItemTypes["appState"]).type === "appState") {
            interaction.appState = prevItem as ChatMessageItemTypes["appState"];
          }
        }
        result.push(interaction);
      }
    });
    return result;
  }, [conversation?.items]);
    
  useEffect(() => {
    setActiveInteraction(interactions[0])
  }, [interactions]);
  
  const updateScore = useCallback((id: string, update: Partial<ScoreModel>) => {
    setScoresMap(
      state => ({
        ...state,
        [id]: {
          ...(state[id] ?? {}),
          ...update
        }
      })
    );
  }, [setScoresMap]);
  
  const getScoresForMessageIds = useCallback((messageIds: ScoreModel["message_ids"]) => {
    if (messageIds === null || messageIds.length === 0) {
      return Object.values(scoresMap).filter(score => score.message_ids === null || score.message_ids.length === 0);
    }
    return Object.values(scoresMap).filter(score => messageIds.every(messageId => score.message_ids?.includes(messageId)));
  }, [scoresMap]);
    
  return <ConversationReviewContext.Provider value={{
    enabled,
    scoresMap, setScoresMap,
    tagsMap, setTagsMap,
    updateScore,
    getScoresForMessageIds,
    conversation, setConversation,
    interactions,
    activeInteraction, setActiveInteraction
  }}>
    {children}
  </ConversationReviewContext.Provider>;
}

export default function ConversationReviewPage() {   
  return <ConversationReviewProvider>
    <ConversationReviewPageContent />  
  </ConversationReviewProvider>;
}

function ConversationReviewPageContent() {
  const L = useLocalizedStrings();
  const getConversation = usePost('/coach/conversation/read');
  const getScores = usePost('/coach/conversation/scores');
  const listTags = usePost('/coach/conversation/tags/list');
  const [loading, setLoading] = useState<boolean>(false);
  const { interactions, setConversation, scoresMap, setScoresMap, setTagsMap, getScoresForMessageIds, enabled, conversation } = useContext(ConversationReviewContext);
  const { conversationId } = useParams() as Record<string, string>;
  const history = useHistory();
  
  const overallScoresSaved = Object.values(scoresMap).filter(score =>
    !score.message_ids || score.message_ids.length === 0
  );
  
  const interactionsWithScores = useMemo(() => { 
    return interactions.filter(interaction => getScoresForMessageIds([interaction.coachMessage, ...interaction.userMessages].map(m => m.id)).length > 0);
  }, [interactions, getScoresForMessageIds])
    
  // load conversation
  useAsyncEffect(async () => {
    try {
      if (!enabled) return;
      setLoading(true);
      
      const result = await getConversation({ id: conversationId });
      if ("error" in result) { 
        throw new Error((result as any).error);
      }
      setConversation({
        ...result,
        created_at: new Date(result.created_at),
        updated_at: new Date(result.updated_at)
      });
    }
    catch (err: any) {
      console.error(err);
    }
    finally { 
      setLoading(false);
    }
  }, [enabled, conversationId]);
  
  // load scores
  useAsyncEffect(async () => {
    try {
      if (!enabled) return;
      const scoresResult = await getScores({ conversationId });
      if (!('error' in scoresResult) && Array.isArray(scoresResult)) {
        setScoresMap(
          scoresResult.reduce(
            (acc, v) => ({
              ...(acc ?? {}),
              [v.id]: v
            }),
            {} as typeof scoresMap
          )
        );
      }
    } catch (scoresError) {
      console.warn('Failed to load existing scores:', scoresError);
    }
  }, [enabled, conversationId])

  // load tags
  useAsyncEffect(async () => {
    try {
      if (!enabled || !conversation || !conversation.items) return;

      // Collect all conversation item IDs from the conversation
      /** Array of all conversation item IDs in this conversation */
      const conversationItemIds: string[] = conversation.items.map(item => item.id).filter((id): id is string => !!id);

      if (conversationItemIds.length === 0) return;

      const tagsResult = await listTags({ conversationItemIds });

      if (!('error' in tagsResult) && tagsResult.tags) {
        setTagsMap(tagsResult.tags);
      }
    } catch (tagsError) {
      console.warn('Failed to load tags:', tagsError);
    }
  }, [enabled, conversation]);

  return <>
    { loading && <Loading /> }
    { enabled
      ? <div className="flex h-full">
        <section className="flex-1 h-full overflow-y-scroll">
          <div className="px-4 py-3 border-b bg-white">
            <Button 
              variant="secondary" 
              size="sm"
              onClick={() => history.push('/ai/config/review')}
            >
              ← Back to Review Queue
            </Button>
          </div>
          
          {/* Overall Conversation Scoring */}
          { interactions.length > 1 && <div className="px-4 py-6 bg-gray-50 border-b">
            <h2 className="text-lg font-semibold">Overall Conversation Score</h2>
            <p className="text-sm">Provide an overall score for the coach's performance in this conversation</p>
            <ConversationOverallScoring 
              compact={overallScoresSaved.length > 0}
            />
          </div>}            
          
          {/* Conversation Header */}
          <div className="px-4 py-4 border-b bg-white sticky top-0">
            <h2 className="text-lg font-semibold text-gray-900">Chat Interaction Scores</h2>
            { interactions.length > 0 && <div className="flex items-center gap-2">
              <ProgressCircle className="text-green-700 w-8 h-8" value={interactionsWithScores.length / interactions.length * 100} />
              <span className="text-sm">{interactionsWithScores.length} of {interactions.length} completed</span>
            </div> }
          </div>
          
          {/* Individual Interactions */}
          <div className="flex flex-col divide-y">
            {
              interactions.map((interaction, i) => <ChatInteractionReview 
                key={i} 
                interaction={interaction} 
              />)
            }
          </div>
          
        </section>
        <ResizablePanel initialWidthPercent={50} displayDensity="compact" label="Survey state" side="right">
          <SurveyPreviewWithAppState />
        </ResizablePanel>
      </div>
      : <Alert variant="warning" className="my-8">{L.ai_coach.dashboard.ai_conversations_not_enabled}</Alert> }
  </>
}

function SurveyPreviewWithAppState() { 
  const [loading, setLoading] = useState<boolean>(false);
  const [sections, setSections] = useState<Sections | undefined>();
  const { activeInteraction } = useContext(ConversationReviewContext);
  const getSubsurvey = usePost("/subsurvey/definition");  
  const shouldFastForward = useRef<boolean>(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const L = useLocalizedStrings();

  const subsurvey = useMemo(() => {
    const fromURL = activeInteraction?.userMessages.find(message => message.meta?.fromURL)?.meta?.fromURL;
    if (!fromURL) return;
    return fromURL.replace(/^(\/p)?\//i, '').split('/')[0];
  }, [activeInteraction?.userMessages]);
  
  useAsyncEffect(async () => {
    if (!subsurvey) {
      return;
    }
    try { 
      setLoading(true);
      const response = await getSubsurvey({form_name: subsurvey});
      setSections(response as Sections);
    }
    catch (err) {
      console.error(err);
    }
    finally {
      setLoading(false);
    }
  }, [subsurvey]);
  
  const visibleQuestion = useMemo(
    () => Object.entries(activeInteraction?.appState?.appState?.questions ?? {}).find(([_, question]) => question.visible), 
    [activeInteraction?.appState]
  );
        
  const applicantContext = useMemo(() => ({
    info: (activeInteraction?.appState?.appState?.info ?? {}) as InfoDict,
    update: () => { },
    refreshInfo: async () => { }
  }), [activeInteraction?.appState]);
  
  // scroll to the question that was visible when the appState was sent
  // TODO this is hacky, we could extend ModularQuestionPage to do this
  useLayoutEffect(() => {
    setTimeout(() => { 
      if (visibleQuestion && containerRef.current) {
        const [visibleQuestionId] = visibleQuestion;
        const visibleQuestionElement = containerRef.current.querySelector(`.survey-question[data-name="${visibleQuestionId}"]`);
        if (visibleQuestionElement) {
          visibleQuestionElement?.scrollIntoView();
        }
      }  
    }, 500) // <= wait for ModulareQuestionPage to render
  }, [visibleQuestion]);
  
  function content() {
    if (loading) {
      return <Loading />
    }
    
    if (!activeInteraction) { 
      return <Alert variant="primary">{L.ai_coach.dashboard.select_interaction}</Alert>
    }
        
    if (!sections) {
      return <Alert variant="warning">{L.ai_coach.dashboard.subsurvey_not_found}</Alert>
    }
            
    return <ModularQuestionPage
      sections={sections as Sections}
      shouldFastForward={shouldFastForward} 
      sequential={true}
      viewInfo={{ readOnly: "true" }}
      noHistory={true}
      submit={async () => {}}
      saveInfo={async (info) => {}}
      saveAuth={(auth) => {}}
      Viewer="screener" />    
  }
  
  return <ApplicantProvider context={applicantContext}>
    <div ref={containerRef} className="px-6 pt-4 -mr-6">
      {content()}
    </div>
  </ApplicantProvider>
}

function ConversationOverallScoring({ compact }: { 
  compact?: boolean
}) {
  const { conversation, getScoresForMessageIds } = useContext(ConversationReviewContext);
  
  if (!conversation) {
    return <Spinner />;
  }
  
  return (
    <div>
      <InlineScoring 
        conversationId={conversation.id.toString()}
        scores={getScoresForMessageIds(null)}
        messageIds={[]} 
        compact={compact || false}
      />
    </div>
  );
}

function ChatInteractionReview(props: { 
  interaction: Interaction,
  onScoreUpdated?: (scores: ScoreRow[]) => void,
  onScoringComplete?: (completed: boolean) => void
}) {
  const config = useContext(PublicConfigurationContext);
  const agentName = config?.capabilities?.aiCoach?.appearance?.name || DEFAULT_AGENT_NAME;
  const avatarUrl = config?.capabilities?.aiCoach?.appearance?.avatarUrl || DEFAULT_AVATAR_URL;
  const { userMessages, coachMessage } = props.interaction;
  const { activeInteraction, setActiveInteraction, getScoresForMessageIds, conversation, tagsMap, setTagsMap } = useContext(ConversationReviewContext);
  const active = activeInteraction === props.interaction;
  
  const messageIds = useMemo(() => {
    return [...userMessages, coachMessage].map(msg => msg.id);
  }, [userMessages, coachMessage]);

  return <div className={`transition-all hover:bg-gray-50 ${active ? 'bg-gray-50 hover:bg-gray-50' : ''}`}>
    <div className="cursor-pointer" onClick={() => setActiveInteraction(props.interaction)}>
      <div className="-mb-12">
        <ChatMessageView
          transcript={
            [...userMessages, coachMessage].map(item => ({
              role: item.role,
              meta: { time: Date.now(), ...(item.meta ?? {}) },
              content: <Card expandable className="bg-transparent border-none">
                <CardBody><MessageContent message={item} /></CardBody>
              </Card>
            }))
          }
          roles={{
            assistant: {
              side: 'left',
              color: 'bg-indigo-100',
              icon: <img src={avatarUrl} alt={agentName} className="rounded-full" />
            },
            user: {
              side: 'right',
              icon: <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center font-semibold text-white">U</div>,
              color: 'bg-gray-100',
            }
          }}
        />
      </div>
    </div>
    
    {conversation && (
      <div className="px-4 pb-4">
        <InlineScoring
          scores={getScoresForMessageIds(messageIds)}
          conversationId={conversation.id.toString()}
          messageIds={messageIds}
          compact={true}
        />

        {/* Tags Section */}
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Tags</h3>
          <div className="space-y-2 border-t pt-2">
            {[...userMessages, coachMessage].map((message) => (
              <div key={message.id} className="flex items-start gap-2">
                <span className="text-xs text-gray-500 font-medium min-w-[70px] pt-1">
                  {message.role === 'user' ? 'User:' : 'Assistant:'}
                </span>
                <InlineTags
                  conversationItemId={message.id}
                  tags={tagsMap[message.id] ?? []}
                  onTagsUpdated={(newTags) => {
                    setTagsMap({
                      ...tagsMap,
                      [message.id]: newTags
                    });
                  }}
                />
              </div>
            ))}

            {/* AppState tags */}
            {props.interaction.appState && (
              <div className="flex items-start gap-2">
                <span className="text-xs text-gray-500 font-medium min-w-[70px] pt-1">
                  App State:
                </span>
                <InlineTags
                  conversationItemId={props.interaction.appState.id}
                  tags={tagsMap[props.interaction.appState.id] ?? []}
                  onTagsUpdated={(newTags) => {
                    setTagsMap({
                      ...tagsMap,
                      [props.interaction.appState!.id]: newTags
                    });
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
};
